import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as api from "../api/client";
import {
  joinRoom,
  setSocketToken,
  type DeckSelection,
  type RoomConnection,
  type RoomSnapshot,
  type SharedDeckView,
} from "../api/rooms";
import type { DeckSummary } from "../api/client";
import { pixelTheme } from "../theme/pixel";
import { factionFor } from "../theme/factions";
import { bevelShadow, defaultBtn, ghostBtn, primaryBtn } from "../theme/styles";
import { inertAttrs, inertOnClick } from "../logic/inert";
import { useActiveGameRedirect } from "../logic/useActiveGameRedirect";

export function RoomPage() {
  const theme = pixelTheme;
  useActiveGameRedirect();
  const { id: roomId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // BattlePage stamps `state: { fromSpectator: true }` when a
  // spectator clicks LEAVE / RETURN TO ROOM, so we skip the
  // "in_game → auto-bounce-to-spectate" rule here once. Otherwise
  // the spectator would never escape the BattlePage view: the
  // navigate back to /r/<id> would re-trigger the bounce.
  const fromSpectator = Boolean(
    (location.state as { fromSpectator?: unknown } | null)?.fromSpectator,
  );

  const [snap, setSnap] = useState<RoomSnapshot | null>(null);
  const [you, setYou] = useState<number | null>(null);
  const [decks, setDecks] = useState<DeckSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [countdownDisplay, setCountdownDisplay] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [roleBusy, setRoleBusy] = useState<number | null>(null);
  // Whether the *current viewer* is a guest. Used to swap LOG OUT-style
  // affordances for a "LOG IN" button. The per-seat is_guest tag for
  // OTHER users comes off the room snapshot.
  const [meIsGuest, setMeIsGuest] = useState(false);

  const connRef = useRef<RoomConnection | null>(null);

  const inviteUrl = useMemo(() => {
    if (!roomId) return "";
    return `${window.location.origin}/r/${roomId}`;
  }, [roomId]);

  useEffect(() => {
    if (!roomId) return;

    let cancelled = false;

    async function attemptJoin() {
      const decksResp = await api.listDecks();
      if (cancelled) return;
      setDecks(decksResp.decks);

      // Stamp `meIsGuest` from /me so the header can offer LOG IN.
      // Cheap (one round-trip) and lets us avoid threading the guest
      // flag through the join response.
      try {
        const me = await api.getMe();
        if (!cancelled) setMeIsGuest(me.user.is_guest);
      } catch {
        // If /me fails for some reason after listDecks succeeded, we
        // just don't show the LOG IN button — non-fatal.
      }

      const { initial, you: yourId, conn } = await joinRoom(roomId!, {
        onState: (s) => setSnap(s),
        onMatchStarted: (p) => {
          const myGameId = p.game_ids[yourId];
          if (myGameId != null) {
            navigate(`/battle/${myGameId}`);
          } else {
            // Not a seated player — we're a spectator. The match's
            // engine_session_id is the BattlePage URL for everyone,
            // so route there with ?spectate=1 so BattlePage knows to
            // suppress action UI.
            navigate(`/battle/${p.engine_session_id}?spectate=1`);
          }
        },
        onMatchEnded: () => {
          // Room re-broadcasts :state shortly; nothing to do here.
        },
        onError: (msg) => setError(msg),
      });

      if (cancelled) {
        conn.leave();
        return;
      }

      connRef.current = conn;
      setSnap(initial);
      setYou(yourId);

      // Late join during an active match: a user who is NOT seated
      // gets auto-routed into the BattlePage as a spectator so they
      // can watch immediately. Skipped when the user navigated here
      // from the spectator BattlePage itself (location.state flag)
      // — they explicitly clicked LEAVE / RETURN TO ROOM and we
      // honour that. Players who re-join (still_in_match) always
      // stay on the room for the post-match flow.
      if (
        !fromSpectator &&
        initial.state === "in_game" &&
        initial.engine_session_id
      ) {
        const seated = initial.players.some((p) => p.user_id === yourId);
        if (!seated) {
          navigate(`/battle/${initial.engine_session_id}?spectate=1`);
        }
      }
    }

    async function go() {
      try {
        await attemptJoin();
      } catch (err) {
        if (cancelled) return;
        // Invite-link visitor without a session: auto-create a guest
        // and retry. No intermediate login screen — the conversion /
        // log-in path is offered later from the room header.
        if (err instanceof api.ApiError && err.status === 401) {
          try {
            const resp = await api.loginAsGuest();
            if (cancelled) return;
            setSocketToken(resp.socket_token);
            setMeIsGuest(true);
            await attemptJoin();
            return;
          } catch (loginErr) {
            if (cancelled) return;
            setError(
              loginErr instanceof Error ? loginErr.message : String(loginErr),
            );
            return;
          }
        }
        setError(err instanceof Error ? err.message : String(err));
      }
    }

    void go();

    return () => {
      cancelled = true;
      // Quiet drop — explicit Leave is the only eviction path.
      connRef.current?.close();
      connRef.current = null;
    };
  }, [roomId, navigate, fromSpectator]);

  // Local-tick countdown; next snapshot resyncs if we drift.
  useEffect(() => {
    if (!snap || snap.state !== "counting_down" || snap.countdown_remaining_ms == null) {
      setCountdownDisplay(null);
      return;
    }
    const deadline = Date.now() + snap.countdown_remaining_ms;
    const tick = () => {
      const remaining = Math.max(0, deadline - Date.now());
      setCountdownDisplay(remaining);
    };
    tick();
    const handle = window.setInterval(tick, 100);
    return () => window.clearInterval(handle);
  }, [snap]);

  const onSelectDeck = useCallback(
    (sel: DeckSelection | null) => {
      connRef.current?.selectDeck(sel);
    },
    [],
  );

  const onToggleReady = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await connRef.current?.toggleReady();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const onShareDeck = useCallback(
    async (d: DeckSummary) => {
      try {
        const { deck } = await api.getDeck(d.id);
        connRef.current?.shareDeck({
          deck: api.toDeckRef(d),
          name: deck.name,
          faction: deck.faction,
          entries: deck.cards.map((c) => [c.card_id, c.count]),
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [],
  );

  const onCopyInvite = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setError("Couldn't copy — your browser may need permission.");
    }
  }, [inviteUrl]);

  const onLeave = useCallback(() => {
    connRef.current?.leave();
    connRef.current = null;
    navigate("/lobby");
  }, [navigate]);

  const onSetRole = useCallback(
    async (targetUserId: number, role: "player" | "spectator") => {
      if (roleBusy != null) return;
      setRoleBusy(targetUserId);
      setError(null);
      try {
        await connRef.current?.setRole(targetUserId, role);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setRoleBusy(null);
      }
    },
    [roleBusy],
  );

  if (!roomId) return null;

  if (error && !snap) {
    return (
      <Centered theme={theme}>
        <div style={{ color: theme.accent }}>{error}</div>
        <button onClick={() => navigate("/lobby")} {...ghostBtn(theme)}>
          BACK TO LOBBY
        </button>
      </Centered>
    );
  }

  if (!snap) {
    return (
      <Centered theme={theme}>
        <div style={{ color: theme.inkDim }}>Joining room…</div>
      </Centered>
    );
  }

  const youPlayer = snap.players.find((p) => p.user_id === you);
  const youSpectator = snap.spectators.find((p) => p.user_id === you);
  const youAreSpectator = !!youSpectator;
  const youAreHost = you != null && you === snap.host_user_id;
  const yourSelectedDeck = youPlayer?.deck;
  const matchInFlight =
    snap.state === "counting_down" ||
    snap.state === "starting" ||
    snap.state === "in_game";
  const playerSeatsFull = snap.players.length >= 2;

  return (
    <div
      style={{
        height: "100%",
        overflowY: "auto",
        background: theme.pageBg,
        color: theme.ink,
        fontFamily: theme.fontBody,
        padding: "24px 20px",
      }}
    >
      <div style={{ maxWidth: 880, margin: "0 auto", display: "grid", gap: 20 }}>
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 14,
              letterSpacing: theme.letterSpacing,
              color: theme.accent,
            }}
          >
            {theme.ornament} ROOM {roomId.toUpperCase()}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onLeave} {...ghostBtn(theme)}>
              LEAVE
            </button>
            {meIsGuest && (
              <button
                onClick={() => {
                  // Send the guest to the standard login form. They can
                  // type either an existing account name (signs them in,
                  // their guest-only XP is left behind on the orphan
                  // row) or a new one. Keeping XP requires the convert
                  // flow, which is offered separately from the lobby
                  // header.
                  connRef.current?.leave();
                  connRef.current = null;
                  const dest = `/room/${roomId}`;
                  navigate(`/login?returnTo=${encodeURIComponent(dest)}`);
                }}
                {...primaryBtn(theme, false)}
              >
                LOG IN
              </button>
            )}
          </div>
        </header>

        <Section theme={theme} title="INVITE LINK">
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              padding: 12,
              background: theme.panel,
              border: `2px solid ${theme.panelEdge}`,
              boxShadow: bevelShadow,
            }}
          >
            <code style={{ flex: 1, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis" }}>
              {inviteUrl}
            </code>
            <button onClick={onCopyInvite} {...defaultBtn(theme, false)}>
              {copied ? "COPIED" : "COPY"}
            </button>
          </div>
        </Section>

        <Section theme={theme} title={`PLAYERS (${snap.players.length}/2)`}>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
            {snap.players.map((p) => (
              <li
                key={p.user_id}
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 12px",
                  background: theme.panel,
                  border: `2px solid ${p.ready ? theme.accent : theme.panelEdge}`,
                  boxShadow: bevelShadow,
                }}
              >
                <span>
                  {p.username}
                  {p.is_guest && (
                    <span style={{ color: theme.inkFaint, marginLeft: 8 }}>(guest)</span>
                  )}
                  {p.user_id === you && (
                    <span style={{ color: theme.inkFaint, marginLeft: 8 }}>(you)</span>
                  )}
                  {p.user_id === snap.host_user_id && (
                    <span style={{ color: theme.inkFaint, marginLeft: 8 }}>· host</span>
                  )}
                </span>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span
                    style={{
                      fontFamily: theme.fontDisplay,
                      fontSize: 11,
                      letterSpacing: theme.letterSpacing,
                      color: p.still_in_match
                        ? theme.inkDim
                        : p.ready
                          ? theme.accent
                          : theme.inkFaint,
                    }}
                  >
                    {p.still_in_match
                      ? "IN MATCH"
                      : p.ready
                        ? "READY"
                        : p.deck
                          ? "PICKED"
                          : "—"}
                  </span>
                  {youAreHost && !matchInFlight && (
                    <button
                      type="button"
                      onClick={inertOnClick(
                        roleBusy === p.user_id,
                        () => void onSetRole(p.user_id, "spectator"),
                      )}
                      {...inertAttrs(roleBusy === p.user_id)}
                      {...ghostBtn(theme)}
                      title="Move this player to the spectator pool"
                    >
                      {roleBusy === p.user_id ? "…" : "TO SPECTATORS"}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Section>

        <Section
          theme={theme}
          title={`SPECTATORS (${snap.spectators.length})`}
        >
          {snap.spectators.length === 0 ? (
            <div style={{ color: theme.inkFaint }}>
              Nobody's watching yet. Share the invite link to bring people in.
            </div>
          ) : (
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                display: "grid",
                gap: 8,
              }}
            >
              {snap.spectators.map((p) => (
                <li
                  key={p.user_id}
                  style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 12px",
                    background: theme.panel,
                    border: `2px solid ${theme.panelEdge}`,
                    boxShadow: bevelShadow,
                  }}
                >
                  <span>
                    {p.username}
                    {p.is_guest && (
                      <span style={{ color: theme.inkFaint, marginLeft: 8 }}>
                        (guest)
                      </span>
                    )}
                    {p.user_id === you && (
                      <span style={{ color: theme.inkFaint, marginLeft: 8 }}>
                        (you)
                      </span>
                    )}
                  </span>
                  {youAreHost && !matchInFlight && (
                    <button
                      type="button"
                      onClick={inertOnClick(
                        playerSeatsFull || roleBusy === p.user_id,
                        () => void onSetRole(p.user_id, "player"),
                      )}
                      {...inertAttrs(playerSeatsFull || roleBusy === p.user_id)}
                      {...defaultBtn(theme, playerSeatsFull)}
                      title={
                        playerSeatsFull
                          ? "Both player seats are taken"
                          : "Move this spectator into a player seat"
                      }
                    >
                      {roleBusy === p.user_id ? "…" : "TO PLAYER"}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>

        {snap.state === "counting_down" && countdownDisplay != null && (
          <div
            style={{
              padding: 16,
              background: theme.panel,
              border: `2px solid ${theme.accent}`,
              textAlign: "center",
              fontFamily: theme.fontDisplay,
              letterSpacing: theme.letterSpacing,
              color: theme.accent,
              fontSize: 18,
            }}
          >
            STARTING IN {Math.ceil(countdownDisplay / 1000)}…
          </div>
        )}

        {youAreSpectator && snap.state !== "in_game" && (
          <div
            style={{
              padding: 12,
              background: theme.panel,
              border: `2px solid ${theme.panelEdge}`,
              color: theme.inkDim,
              fontFamily: theme.fontDisplay,
              fontSize: 12,
              letterSpacing: theme.letterSpacing,
              boxShadow: bevelShadow,
            }}
          >
            {theme.ornament} YOU'RE SPECTATING — the match will open
            automatically when the players are ready.
          </div>
        )}

        {youAreSpectator &&
          snap.state === "in_game" &&
          snap.engine_session_id && (
            <div
              style={{
                padding: 12,
                background: theme.panel,
                border: `2px solid ${theme.accent}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                boxShadow: bevelShadow,
              }}
            >
              <span
                style={{
                  fontFamily: theme.fontDisplay,
                  fontSize: 12,
                  letterSpacing: theme.letterSpacing,
                  color: theme.accent,
                }}
              >
                {theme.ornament} MATCH IN PROGRESS
              </span>
              <button
                onClick={() =>
                  navigate(`/battle/${snap.engine_session_id}?spectate=1`)
                }
                {...primaryBtn(theme, false)}
              >
                JOIN MATCH
              </button>
            </div>
          )}

        {!youAreSpectator && (
        <>
        <Section theme={theme} title="YOUR DECKS">
          {decks.length === 0 ? (
            <div style={{ color: theme.inkFaint }}>No decks yet — head to the lobby to build one.</div>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
              {decks.map((d) => {
                const fc = factionFor(d.faction).color;
                const isSelected =
                  yourSelectedDeck?.kind === "owned" &&
                  yourSelectedDeck.deck === api.toDeckRef(d);
                return (
                  <li
                    key={d.id}
                    style={{
                      display: "flex",
                      gap: 12,
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "10px 12px",
                      background: theme.panel,
                      border: `2px solid ${isSelected ? theme.accent : theme.panelEdge}`,
                      borderLeft: `6px solid ${fc}`,
                      boxShadow: bevelShadow,
                    }}
                  >
                    <div style={{ display: "grid", gap: 2 }}>
                      <div style={{ fontSize: 18 }}>{d.name}</div>
                      <div
                        style={{
                          fontFamily: theme.fontDisplay,
                          fontSize: 10,
                          letterSpacing: theme.letterSpacing,
                          color: theme.inkFaint,
                        }}
                      >
                        {d.faction.toUpperCase()}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        type="button"
                        onClick={inertOnClick(!!youPlayer?.ready, () =>
                          onSelectDeck(
                            isSelected ? null : { kind: "owned", deck: api.toDeckRef(d) },
                          ),
                        )}
                        {...inertAttrs(!!youPlayer?.ready)}
                        {...(isSelected ? defaultBtn(theme, false) : primaryBtn(theme, false))}
                      >
                        {isSelected ? "SELECTED" : "SELECT"}
                      </button>
                      <button
                        type="button"
                        onClick={inertOnClick(!!youPlayer?.ready, () => void onShareDeck(d))}
                        {...inertAttrs(!!youPlayer?.ready)}
                        {...defaultBtn(theme, false)}
                        title="Share this deck so other players in the room can play it"
                      >
                        SHARE
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>

        {snap.shared_decks.some((d) => d.shared_by !== you) && (
          <Section theme={theme} title="SHARED BY OTHERS">
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
              {snap.shared_decks
                .filter((d) => d.shared_by !== you)
                .map((d) => (
                  <SharedDeckRow
                    key={`${d.shared_by}:${d.deck}`}
                    deck={d}
                    selected={
                      yourSelectedDeck?.kind === "shared" &&
                      yourSelectedDeck.shared_by === d.shared_by &&
                      yourSelectedDeck.deck === d.deck
                    }
                    locked={!!youPlayer?.ready}
                    onSelect={(sel) => onSelectDeck(sel)}
                    theme={theme}
                  />
                ))}
            </ul>
          </Section>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={inertOnClick(
              busy || (!youPlayer?.ready && !yourSelectedDeck) || snap.state === "in_game",
              () => void onToggleReady(),
            )}
            {...inertAttrs(
              busy || (!youPlayer?.ready && !yourSelectedDeck) || snap.state === "in_game",
            )}
            {...primaryBtn(
              theme,
              busy || (!youPlayer?.ready && !yourSelectedDeck) || snap.state === "in_game",
            )}
          >
            {youPlayer?.ready ? "UNREADY" : "READY"}
          </button>
        </div>
        </>
        )}

        {error && (
          <div
            style={{
              padding: 12,
              background: theme.panel,
              border: `2px solid ${theme.accent}`,
              color: theme.accent,
            }}
          >
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

function SharedDeckRow({
  deck,
  selected,
  locked,
  onSelect,
  theme,
}: {
  deck: SharedDeckView;
  selected: boolean;
  locked: boolean;
  onSelect: (sel: DeckSelection | null) => void;
  theme: typeof pixelTheme;
}) {
  const fc = factionFor(deck.faction).color;
  return (
    <li
      style={{
        display: "flex",
        gap: 12,
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 12px",
        background: theme.panel,
        border: `2px solid ${selected ? theme.accent : theme.panelEdge}`,
        borderLeft: `6px solid ${fc}`,
        boxShadow: bevelShadow,
      }}
    >
      <div style={{ display: "grid", gap: 2 }}>
        <div style={{ fontSize: 18 }}>{deck.name}</div>
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 10,
            letterSpacing: theme.letterSpacing,
            color: theme.inkFaint,
          }}
        >
          {deck.faction.toUpperCase()} · shared by {deck.shared_by_username}
        </div>
      </div>
      <button
        type="button"
        onClick={inertOnClick(locked, () =>
          onSelect(
            selected
              ? null
              : { kind: "shared", shared_by: deck.shared_by, deck: deck.deck },
          ),
        )}
        {...inertAttrs(locked)}
        {...(selected ? defaultBtn(theme, false) : primaryBtn(theme, false))}
      >
        {selected ? "SELECTED" : "SELECT"}
      </button>
    </li>
  );
}

function Section({
  title,
  children,
  theme,
}: {
  title: string;
  children: React.ReactNode;
  theme: typeof pixelTheme;
}) {
  return (
    <section style={{ display: "grid", gap: 8 }}>
      <h2
        style={{
          margin: 0,
          fontFamily: theme.fontDisplay,
          fontSize: 12,
          letterSpacing: theme.letterSpacing,
          color: theme.inkDim,
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function Centered({
  theme,
  children,
}: {
  theme: typeof pixelTheme;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        height: "100%",
        overflowY: "auto",
        background: theme.pageBg,
        color: theme.ink,
        fontFamily: theme.fontBody,
        display: "grid",
        placeItems: "center",
        gap: 12,
      }}
    >
      {children}
    </div>
  );
}
